import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { RefreshCw, TrendingUp, Lightbulb, BarChart2, Calendar, Clock, CheckCircle, AlertTriangle, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

const BAR_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500',
  'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
];

const PRIORITY_STYLES = {
  High: 'bg-red-100 text-red-700 border-red-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Low: 'bg-green-100 text-green-700 border-green-200'
};

const PRIORITY_ORDER = { High: 0, Medium: 1, Low: 2 };

const formatCurrency = (v) =>
  `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MetricChip = ({ label, value, icon: Icon, color }) => (
  <div className={`rounded-xl border p-4 flex flex-col gap-1 ${color}`}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-60">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <p className="text-2xl font-bold leading-none">{value}</p>
  </div>
);

const getRpnBadge = (rpn) => {
  if (rpn > 400) return 'bg-red-100 text-red-800 border-red-300';
  if (rpn > 200) return 'bg-orange-100 text-orange-800 border-orange-300';
  if (rpn > 100) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-green-100 text-green-800 border-green-300';
};

const getRpnRowBg = (rpn) => rpn > 400 ? 'bg-red-50' : '';

const getDot = (val) => {
  if (val >= 7) return 'inline-block w-2 h-2 rounded-full bg-red-500 mr-1';
  if (val >= 4) return 'inline-block w-2 h-2 rounded-full bg-yellow-400 mr-1';
  return 'inline-block w-2 h-2 rounded-full bg-green-500 mr-1';
};

const OWNER_COLORS = {
  'Manufacturing': 'bg-blue-100 text-blue-800',
  'Logistics': 'bg-purple-100 text-purple-800',
  'QC': 'bg-teal-100 text-teal-800',
  'Customer Service': 'bg-orange-100 text-orange-800',
  'Product Design': 'bg-indigo-100 text-indigo-800',
};

const OwnerChip = ({ owner }) => {
  const cls = OWNER_COLORS[owner] || 'bg-gray-100 text-gray-800';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{owner}</span>;
};

const AIInsightsPanel = ({ dateFrom, dateTo, initialSubTab, onSubTabChange }) => {
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab || 'Quality Insights');

  // Quality Insights state
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isHistorical, setIsHistorical] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom);
  const [localTo, setLocalTo] = useState(dateTo);

  // FMEA state
  const [fmeaData, setFmeaData] = useState(null);
  const [fmeaLoading, setFmeaLoading] = useState(false);
  const [fmeaLocalFrom, setFmeaLocalFrom] = useState(dateFrom);
  const [fmeaLocalTo, setFmeaLocalTo] = useState(dateTo);
  const [expandedCountermeasures, setExpandedCountermeasures] = useState({});

  useEffect(() => {
    setLocalFrom(dateFrom);
    setLocalTo(dateTo);
    setFmeaLocalFrom(dateFrom);
    setFmeaLocalTo(dateTo);
  }, [dateFrom, dateTo]);

  // Sync sub-tab from parent
  useEffect(() => {
    if (initialSubTab && initialSubTab !== activeSubTab) {
      setActiveSubTab(initialSubTab);
    }
  }, [initialSubTab]);

  const handleSubTabChange = (tab) => {
    setActiveSubTab(tab);
    if (onSubTabChange) onSubTabChange(tab);
  };

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/insights/history');
      setHistory(res.data || []);
    } catch { }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const saveToHistory = async (data, from, to) => {
    try {
      await api.post('/insights/save', { generated_at: data.generated_at, from_date: from || null, to_date: to || null, result: data });
      fetchHistory();
    } catch { }
  };

  const runAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setAnalysis(null);
    setSelectedHistory('');
    setIsHistorical(false);
    try {
      const params = { refresh: forceRefresh };
      if (localFrom) params.from_date = localFrom;
      if (localTo) params.to_date = localTo;
      const res = await api.get('/insights/analysis', { params });
      setAnalysis(res.data);
      if (!res.data.cached && !res.data.error) {
        await saveToHistory(res.data, localFrom, localTo);
        toast.success('Analysis generated and saved');
      } else if (res.data.cached) {
        toast.info('Showing cached analysis (< 10 min old)');
      }
    } catch { toast.error('Failed to fetch insights'); }
    finally { setLoading(false); }
  };

  const loadHistoryItem = async (historyId) => {
    if (!historyId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/insights/history/${historyId}`);
      setAnalysis(res.data.result);
      setIsHistorical(true);
      toast.success('Loaded historical analysis');
    } catch { toast.error('Failed to load historical analysis'); }
    finally { setLoadingHistory(false); }
  };

  const runFmea = async (forceRefresh = false) => {
    setFmeaLoading(true);
    setFmeaData(null);
    try {
      const params = { refresh: forceRefresh };
      if (fmeaLocalFrom) params.from_date = fmeaLocalFrom;
      if (fmeaLocalTo) params.to_date = fmeaLocalTo;
      const res = await api.get('/insights/fmea', { params });
      setFmeaData(res.data);
      if (res.data.cached) toast.info('Showing cached FMEA (< 10 min old)');
      else if (!res.data.error) toast.success('FMEA analysis complete');
    } catch { toast.error('Failed to run FMEA analysis'); }
    finally { setFmeaLoading(false); }
  };

  const handleExportFmea = async () => {
    try {
      const response = await api.get('/insights/fmea/export', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'fmea_analysis.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('FMEA CSV exported');
    } catch { toast.error('Export failed — run FMEA analysis first'); }
  };

  const sortedRecommendations = analysis?.preventive_recommendations
    ? [...analysis.preventive_recommendations].sort(
        (a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3)
      )
    : [];

  return (
    <div className="space-y-6" data-testid="ai-insights-panel">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto" data-testid="insights-subtabs">
        {['Quality Insights', 'FMEA Analysis'].map(tab => (
          <button
            key={tab}
            onClick={() => handleSubTabChange(tab)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeSubTab === tab
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`subtab-${tab.toLowerCase().replace(/ /g, '-')}`}
          >
            {tab === 'Quality Insights' && <TrendingUp className="h-4 w-4" />}
            {tab === 'FMEA Analysis' && <AlertTriangle className="h-4 w-4" />}
            {tab}
          </button>
        ))}
      </div>

      {/* ── QUALITY INSIGHTS TAB ── */}
      {activeSubTab === 'Quality Insights' && (
        <>
          {/* Header Row */}
          <div className="flex flex-wrap items-end gap-4 p-5 bg-card border border-border rounded-xl shadow-sm">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                AI-Powered Quality Insights
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Powered by Claude AI — identifies patterns and preventive actions from resolved tickets
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">From</Label>
                <Input type="date" value={localFrom} onChange={(e) => setLocalFrom(e.target.value)}
                  className="w-36 text-sm" data-testid="insights-date-from" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">To</Label>
                <Input type="date" value={localTo} onChange={(e) => setLocalTo(e.target.value)}
                  className="w-36 text-sm" data-testid="insights-date-to" />
              </div>
              <Button onClick={() => runAnalysis(true)} disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto h-11"
                data-testid="refresh-analysis-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Analysing...' : 'Refresh Analysis'}
              </Button>
            </div>
          </div>

          {/* History Selector */}
          {history.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-1" data-testid="history-select-row">
              <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <Label className="text-sm whitespace-nowrap">View Previous Analysis:</Label>
              <Select value={selectedHistory} onValueChange={(v) => { setSelectedHistory(v); loadHistoryItem(v); }} data-testid="history-select">
                <SelectTrigger className="w-full sm:w-72 text-sm">
                  <SelectValue placeholder="Select a past analysis..." />
                </SelectTrigger>
                <SelectContent>
                  {history.filter(h => !h.type || h.type === 'quality').map(h => (
                    <SelectItem key={h.history_id} value={h.history_id}>
                      {format(new Date(h.generated_at), 'dd MMM yyyy, HH:mm')} — {h.tickets_analyzed_count} tickets
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loadingHistory && <span className="text-xs text-muted-foreground">Loading...</span>}
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="insights-loading">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600" />
              <p className="text-base font-medium text-muted-foreground">Claude is analysing your ticket data...</p>
              <p className="text-sm text-muted-foreground/70">This may take 10–20 seconds</p>
            </div>
          )}

          {!loading && analysis?.error === 'not_enough_data' && (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-center" data-testid="not-enough-data">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                <BarChart2 className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-700">Not enough data yet</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  Resolve at least 3 tickets with root cause analysis filled in to generate insights.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>View Tickets</Button>
            </div>
          )}

          {!loading && analysis?.error === 'ai_unavailable' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center" data-testid="ai-unavailable">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="text-base font-semibold">AI analysis temporarily unavailable</p>
              <p className="text-sm text-muted-foreground">Please try again in a moment.</p>
              <Button variant="outline" size="sm" onClick={() => runAnalysis(true)}>Retry</Button>
            </div>
          )}

          {!loading && analysis && !analysis.error && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-1" data-testid="analysis-meta">
                {isHistorical
                  ? <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">Historical analysis</Badge>
                  : analysis.cached
                    ? <Badge variant="outline" className="text-xs">Cached result</Badge>
                    : <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Fresh analysis</Badge>
                }
                {analysis.generated_at && (
                  <span>Generated: {format(new Date(analysis.generated_at), 'dd MMM yyyy, HH:mm:ss')}</span>
                )}
              </div>

              {analysis.summary_paragraph && (
                <Card className="bg-blue-50 border-blue-200" data-testid="summary-card">
                  <CardContent className="pt-5 pb-5">
                    <p className="text-sm leading-relaxed text-blue-900">{analysis.summary_paragraph}</p>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="insights-metrics-grid">
                <MetricChip label="Tickets Analysed" value={analysis.total_tickets_analyzed || 0} icon={BarChart2} color="bg-slate-50 border-slate-200 text-slate-800" />
                <MetricChip label="Total Cost Incurred" value={formatCurrency(analysis.cost_analysis?.total_cost_incurred)} icon={TrendingUp} color="bg-green-50 border-green-200 text-green-800" />
                <MetricChip label="Avg Cost / Ticket" value={formatCurrency(analysis.cost_analysis?.avg_cost_per_ticket)} icon={Calendar} color="bg-blue-50 border-blue-200 text-blue-800" />
                <MetricChip label="Avg Resolution Time" value={`${(analysis.cost_analysis?.avg_time_per_ticket_hours || 0).toFixed(1)}h`} icon={Clock} color="bg-purple-50 border-purple-200 text-purple-800" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card data-testid="root-causes-card">
                  <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Top Root Causes</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {(analysis.top_root_causes || []).map((rc, idx) => (
                      <div key={idx} data-testid={`root-cause-bar-${idx}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium truncate pr-2">{rc.category}</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{rc.count} ({rc.percentage})</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${BAR_COLORS[idx % BAR_COLORS.length]}`} style={{ width: rc.percentage }} />
                        </div>
                        {rc.common_theme && <p className="text-xs text-muted-foreground mt-0.5 italic">{rc.common_theme}</p>}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card data-testid="recurring-patterns-card">
                  <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Recurring Patterns</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {(analysis.recurring_patterns || []).length === 0 && <p className="text-sm text-muted-foreground">No recurring patterns detected.</p>}
                    {(analysis.recurring_patterns || []).map((p, idx) => (
                      <div key={idx} className="p-3 border border-border rounded-lg space-y-1.5" data-testid={`pattern-card-${idx}`}>
                        <p className="text-sm font-medium">{p.pattern}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">Affects {p.frequency} ticket{p.frequency !== 1 ? 's' : ''}</Badge>
                          {p.estimated_total_cost > 0 && (
                            <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">Cost: {formatCurrency(p.estimated_total_cost)}</Badge>
                          )}
                        </div>
                        {(p.tickets_affected || []).length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {p.tickets_affected.map((tid) => (
                              <button key={tid} onClick={() => navigate(`/admin/ticket/${tid}`)}
                                className="text-xs text-blue-600 hover:underline font-mono" data-testid={`pattern-ticket-link-${tid}`}>
                                {String(tid).substring(0, 8)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <Card data-testid="recommendations-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-500" />
                    <CardTitle className="text-base font-semibold">Preventive Recommendations</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sortedRecommendations.length === 0 && <p className="text-sm text-muted-foreground">No recommendations available.</p>}
                  {sortedRecommendations.map((rec, idx) => (
                    <div key={idx} className="flex gap-3 pb-4 border-b border-border last:border-0 last:pb-0" data-testid={`recommendation-${idx}`}>
                      <div className="pt-0.5 flex-shrink-0">
                        <Badge className={`text-xs border ${PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES.Low}`} data-testid={`rec-priority-${idx}`}>{rec.priority}</Badge>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{rec.action}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{rec.rationale}</p>
                        {rec.estimated_impact && <p className="text-xs text-muted-foreground/70 italic mt-1">{rec.estimated_impact}</p>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card data-testid="countermeasure-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <CardTitle className="text-base font-semibold">Countermeasure Effectiveness</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {(analysis.countermeasure_effectiveness || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No countermeasure data available.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="countermeasure-table">
                        <thead>
                          <tr className="border-b border-border text-left">
                            <th className="pb-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground pr-4">Countermeasure</th>
                            <th className="pb-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground pr-4 text-center">Times Suggested</th>
                            <th className="pb-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground pr-4 text-center">Issue Recurred?</th>
                            <th className="pb-2 font-semibold text-xs uppercase tracking-wide text-muted-foreground">Verdict</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(analysis.countermeasure_effectiveness || []).map((cm, idx) => (
                            <tr key={idx} className="border-b border-border last:border-0" data-testid={`cm-row-${idx}`}>
                              <td className="py-2.5 pr-4 font-medium">{cm.countermeasure}</td>
                              <td className="py-2.5 pr-4 text-center">{cm.times_suggested}</td>
                              <td className="py-2.5 pr-4 text-center">
                                {cm.issue_recurred ? <span className="text-amber-600 font-medium">Yes</span> : <span className="text-green-600 font-medium">No</span>}
                              </td>
                              <td className="py-2.5">
                                {cm.issue_recurred
                                  ? <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Needs Review</span>
                                  : <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><CheckCircle className="h-3.5 w-3.5" /> Effective</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <p className="text-xs text-muted-foreground text-center pb-4" data-testid="insights-footer">
                Analysis powered by Claude AI. Refresh to include newly resolved tickets.
              </p>
            </div>
          )}

          {!loading && !analysis && (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="insights-empty">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
              <div>
                <p className="text-lg font-semibold">No analysis loaded</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Refresh Analysis" to generate AI-powered insights.</p>
              </div>
              <Button onClick={() => runAnalysis(false)} className="bg-blue-600 hover:bg-blue-700 text-white h-11">
                Generate Insights
              </Button>
            </div>
          )}
        </>
      )}

      {/* ── FMEA ANALYSIS TAB ── */}
      {activeSubTab === 'FMEA Analysis' && (
        <>
          {/* Header */}
          <div className="flex flex-wrap items-end gap-4 p-5 bg-card border border-border rounded-xl shadow-sm">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                FMEA Risk Analysis
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Failure Mode and Effects Analysis — ranked by Risk Priority Number (RPN)
              </p>
              {fmeaData && !fmeaData.error && fmeaData.generated_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Last generated: {format(new Date(fmeaData.generated_at), 'dd MMM yyyy, HH:mm')}
                  {fmeaData.cached && <span className="ml-2 text-blue-600">(cached)</span>}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">From</Label>
                <Input type="date" value={fmeaLocalFrom} onChange={(e) => setFmeaLocalFrom(e.target.value)} className="w-36 text-sm" data-testid="fmea-date-from" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">To</Label>
                <Input type="date" value={fmeaLocalTo} onChange={(e) => setFmeaLocalTo(e.target.value)} className="w-36 text-sm" data-testid="fmea-date-to" />
              </div>
              <Button onClick={() => runFmea(true)} disabled={fmeaLoading}
                className="bg-orange-600 hover:bg-orange-700 text-white w-full sm:w-auto h-11"
                data-testid="run-fmea-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${fmeaLoading ? 'animate-spin' : ''}`} />
                {fmeaLoading ? 'Analysing...' : 'Run FMEA Analysis'}
              </Button>
            </div>
          </div>

          {/* Loading */}
          {fmeaLoading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="fmea-loading">
              <div className="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin border-t-orange-600" />
              <p className="text-base font-medium text-muted-foreground">Claude is performing FMEA analysis...</p>
              <p className="text-sm text-muted-foreground/70">This may take 10–20 seconds</p>
            </div>
          )}

          {/* Not enough data */}
          {!fmeaLoading && fmeaData?.error === 'not_enough_data' && (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-center" data-testid="fmea-not-enough-data">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                <BarChart2 className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-700">Not enough data yet</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  Resolve at least 3 tickets with complete resolution data to run FMEA.
                </p>
              </div>
            </div>
          )}

          {/* AI Unavailable */}
          {!fmeaLoading && fmeaData?.error === 'ai_unavailable' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center" data-testid="fmea-ai-unavailable">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="text-base font-semibold">FMEA analysis temporarily unavailable</p>
              <Button variant="outline" size="sm" onClick={() => runFmea(true)}>Retry</Button>
            </div>
          )}

          {/* Empty state */}
          {!fmeaLoading && !fmeaData && (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="fmea-empty">
              <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
              <div>
                <p className="text-lg font-semibold">No FMEA analysis loaded</p>
                <p className="text-sm text-muted-foreground mt-1">Click "Run FMEA Analysis" to identify failure modes and risk priorities.</p>
              </div>
              <Button onClick={() => runFmea(false)} className="bg-orange-600 hover:bg-orange-700 text-white h-11">
                Run FMEA Analysis
              </Button>
            </div>
          )}

          {/* FMEA Results */}
          {!fmeaLoading && fmeaData && !fmeaData.error && (
            <div className="space-y-6" data-testid="fmea-results">
              {/* Summary Banner */}
              <Card className="bg-slate-50 border-slate-200" data-testid="fmea-summary-banner">
                <CardContent className="pt-5 pb-5">
                  <p className="text-sm leading-relaxed text-slate-800 mb-3">{fmeaData.analysis_summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {fmeaData.highest_risk_theme && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold border border-red-200">
                        Highest Risk: {fmeaData.highest_risk_theme}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold border border-blue-200">
                      {fmeaData.total_tickets_analysed} tickets analysed
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* RPN Legend */}
              <div className="flex flex-wrap gap-3 text-xs" data-testid="rpn-legend">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 text-green-800 border border-green-200 font-medium">RPN 1–100: Low Risk</span>
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200 font-medium">RPN 101–200: Medium Risk</span>
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200 font-medium">RPN 201–400: High Risk</span>
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 text-red-800 border border-red-200 font-medium">RPN 401+: Critical Risk</span>
              </div>

              {/* FMEA Table */}
              <Card data-testid="fmea-table-card">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth: '900px' }} data-testid="fmea-table">
                      <thead className="border-b border-border bg-muted/30">
                        <tr className="text-left">
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground sticky left-0 bg-muted/30 z-10 w-8">#</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground sticky left-8 bg-muted/30 z-10 min-w-[140px]">Failure Mode</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground min-w-[120px]">Effect</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground min-w-[140px]">Potential Cause</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-center w-12">S</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-center w-12">O</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-center w-12">D</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-center w-16">RPN</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-center w-16">Tickets</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground text-right w-24">Avg Cost</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground min-w-[180px]">Countermeasure</th>
                          <th className="py-3 px-3 font-semibold text-xs uppercase tracking-wide text-muted-foreground min-w-[120px]">Owner</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(fmeaData.fmea_items || []).map((item, idx) => {
                          const rpn = item.rpn || 0;
                          const isExpanded = expandedCountermeasures[idx];
                          return (
                            <tr key={idx} className={`border-b border-border last:border-0 ${getRpnRowBg(rpn)}`} data-testid={`fmea-row-${idx}`}>
                              <td className={`py-3 px-3 text-xs text-muted-foreground sticky left-0 z-10 ${getRpnRowBg(rpn) || 'bg-white'}`}>{idx + 1}</td>
                              <td className={`py-3 px-3 font-semibold text-sm sticky left-8 z-10 ${getRpnRowBg(rpn) || 'bg-white'}`}>{item.failure_mode}</td>
                              <td className="py-3 px-3 text-sm text-muted-foreground">{item.effect}</td>
                              <td className="py-3 px-3 text-sm text-muted-foreground">{item.potential_cause}</td>
                              <td className="py-3 px-3 text-center">
                                <span className={getDot(item.severity)} />
                                <span className="text-sm font-medium">{item.severity}</span>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className={getDot(item.occurrence)} />
                                <span className="text-sm font-medium">{item.occurrence}</span>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className={getDot(item.detection)} />
                                <span className="text-sm font-medium">{item.detection}</span>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <Badge className={`text-xs border font-bold ${getRpnBadge(rpn)}`} data-testid={`rpn-badge-${idx}`}>{rpn}</Badge>
                              </td>
                              <td className="py-3 px-3 text-center text-sm">{item.ticket_count}</td>
                              <td className="py-3 px-3 text-right text-sm font-medium">₹{(item.avg_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}</td>
                              <td className="py-3 px-3 text-sm">
                                <div>
                                  <p className={isExpanded ? '' : 'line-clamp-2'}>{item.countermeasure}</p>
                                  {item.countermeasure && item.countermeasure.length > 80 && (
                                    <button
                                      onClick={() => setExpandedCountermeasures(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                      className="text-xs text-blue-600 hover:underline mt-0.5 flex items-center gap-1"
                                      data-testid={`expand-countermeasure-${idx}`}
                                    >
                                      {isExpanded ? <><ChevronUp className="h-3 w-3" />Less</> : <><ChevronDown className="h-3 w-3" />Read more</>}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-3"><OwnerChip owner={item.countermeasure_owner} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Export Button */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleExportFmea} className="h-11" data-testid="export-fmea-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export FMEA as CSV
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AIInsightsPanel;
